/**
 * SocketIO Hooks
 *
 * Custom React hooks for subscribing to socket events.
 * Updated for v2 backend event names and payloads.
 */

import { useEffect, useState, useCallback, useRef } from 'react';

import { useSocketIOContext } from './SocketIOContext';
import { SocketIOEvent, SocketIONamespace } from './types';

import type {
  SocketIOEventData,
  NotificationEventData,
  UserStatusEventData,
} from './types';

// ============================================================================
// Generic Event Hook
// ============================================================================

/**
 * Subscribe to a specific socket event
 *
 * @param eventName - The event name to listen for
 * @param callback - Optional callback to handle the event
 * @param namespace - Optional namespace filter (required when event name is shared across namespaces)
 * @returns The latest event data
 */
export function useSocketIOEvent<T = unknown>(
  eventName: SocketIOEvent | string,
  callback?: (data: T) => void,
  namespace?: SocketIONamespace | string
): T | null {
  const { subscribe } = useSocketIOContext();
  const [lastEvent, setLastEvent] = useState<T | null>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const unsubscribe = subscribe((event: SocketIOEventData) => {
      if (event.event === eventName) {
        // If namespace filter is provided, check it
        if (namespace && event.namespace !== namespace) return;

        const data = event.data as T;
        setLastEvent(data);
        callbackRef.current?.(data);
      }
    });

    return unsubscribe;
  }, [eventName, namespace, subscribe]);

  return lastEvent;
}

// ============================================================================
// Typed Event Hooks
// ============================================================================

/**
 * Subscribe to notification events (/notification namespace — new_notification)
 */
export function useNotifications(callback?: (notification: NotificationEventData) => void) {
  return useSocketIOEvent<NotificationEventData>(
    SocketIOEvent.NewNotification,
    callback,
    SocketIONamespace.Notification
  );
}

/**
 * Subscribe to user status changes (/user namespace — status)
 */
export function useUserStatus(callback?: (data: UserStatusEventData) => void) {
  return useSocketIOEvent<UserStatusEventData>(
    SocketIOEvent.UserStatus,
    callback,
    SocketIONamespace.User
  );
}

// ============================================================================
// Connection Status Hook
// ============================================================================

/**
 * Get socket connection status and controls
 */
export function useSocketIO() {
  const { status, isConnected, connect, disconnect, connectedNamespaces } = useSocketIOContext();

  const reconnect = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      await connect(token);
    }
  }, [connect]);

  return {
    status,
    isConnected,
    connectedNamespaces,
    connect,
    disconnect,
    reconnect,
  };
}

// ============================================================================
// All Events Hook (for debugging)
// ============================================================================

/**
 * Subscribe to all socket events (useful for debugging)
 */
export function useAllSocketIOEvents(callback: (event: SocketIOEventData) => void) {
  const { subscribe, isConnected } = useSocketIOContext();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe((event) => {
      callbackRef.current(event);
    });

    return unsubscribe;
  }, [subscribe, isConnected]);
}
